import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend, ReferenceDot,
} from 'recharts';
import WidgetShell from '../WidgetShell';
import TradeInsightsPanel from '../../insights/TradeInsightsPanel';
import { getStrategySheet } from '../strategyDataCache';
import { resolveStrategyDisplayName } from '../resolveStrategyDisplayName';
import { buildChartData, extractWeeklyData } from '../../../utils/strategySheetDerivations';
import { collapseStrategies, sideTagClass, sideTagText } from '../../../utils/optionStrategies';
import {
  usd, pct, tone, shortDate, niceDate, rangeLabel, UP, DOWN, PALETTE, brandFor, BrokerMark,
} from './myWidgetUi';
import {
  ALL_ACCOUNTS,
  brokerLabel,
  cleanAccountName,
  describeAccountChoice,
  getMyAccountBalances,
  getMyLiveAccounts,
  getMyPerformance,
  getMyPositions,
  getMyTrades,
  refreshMyAccounts,
} from './myDataApi';
import {
  SourceLine, InsightModal, AccountBreakdown, HoldingsInsight, TradesInsight, PositionsInsight,
} from './MyWidgetInsights';
import './MyDataWidgets.css';

// The broker picker from My Portfolio, opened right from an empty widget.
const TradeIntegrationModal = lazy(() => import('../../vectorDashboard/TradeIntegrationModal'));
// Fired after an account is connected from a widget, so every widget reloads.
const ACCOUNTS_CHANGED = 'risklo:my-accounts-changed';

/*
 * Dashboard widgets fed by the user's OWN data (My Portfolio accounts and live
 * Trade Watcher accounts). Pro: when the account isn't on Pro they show a short
 * upgrade note instead of data.
 */

function useReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/** Ease a number toward its new value. */
function useCountUp(target, duration = 700) {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(Number.isFinite(target) ? target : 0);
  const from = useRef(value);
  useEffect(() => {
    if (!Number.isFinite(target)) return undefined;
    if (reduced) { setValue(target); from.current = target; return undefined; }
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const v = a + (target - a) * (1 - (1 - t) ** 3);
      from.current = v;
      setValue(v);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, reduced]);
  return value;
}

/** Load with loading / error / Pro states; reloads when deps change. */
function useMyData(loader, deps) {
  const [state, setState] = useState({ loading: true });
  const [token, setToken] = useState(0);
  // Reload when an account is connected from any widget.
  useEffect(() => {
    const bump = () => setToken((t) => t + 1);
    window.addEventListener(ACCOUNTS_CHANGED, bump);
    return () => window.removeEventListener(ACCOUNTS_CHANGED, bump);
  }, []);
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: !s.data, refreshing: Boolean(s.data), error: null }));
    loader()
      .then((data) => { if (live) setState({ loading: false, data }); })
      .catch((err) => { if (live) setState({ loading: false, error: err?.message || 'Couldn’t load your data', requiresPro: Boolean(err?.requiresPro) }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, token]);
  return { ...state, retry: () => setToken((t) => t + 1) };
}

/** A setting the widget can change itself (range, grouping): saved when allowed, local otherwise. */
function useWidgetSetting(config, key, fallback, onConfigChange) {
  const [local, setLocal] = useState(config?.[key] ?? fallback);
  useEffect(() => { setLocal(config?.[key] ?? fallback); }, [config, key, fallback]);
  const set = (v) => {
    setLocal(v);
    if (onConfigChange) onConfigChange({ [key]: v });
  };
  return [local, set];
}

function ProNote() {
  return (
    <div className="mydw-state">
      <strong>RiskLo Pro</strong>
      <span>Connect your own accounts and see them here with Pro.</span>
    </div>
  );
}

function NoAccounts() {
  const [open, setOpen] = useState(false);
  const close = async () => {
    setOpen(false);
    try { await refreshMyAccounts(); } catch (_e) { /* the widgets retry on their own */ }
    window.dispatchEvent(new Event(ACCOUNTS_CHANGED));
  };
  return (
    <div className="mydw-state">
      <strong>No accounts connected yet</strong>
      <span>Connect a brokerage and it shows up here.</span>
      <button type="button" className="mydw-connect-btn" onClick={() => setOpen(true)}>Connect accounts</button>
      {open ? createPortal(
        <Suspense fallback={null}>
          <TradeIntegrationModal isOpen onClose={close} onSynced={() => window.dispatchEvent(new Event(ACCOUNTS_CHANGED))} />
        </Suspense>,
        document.body,
      ) : null}
    </div>
  );
}

/** Clicks on the widget (not on its own buttons or chart controls) open its detail view. */
function openUnlessControl(onOpen) {
  return (e) => {
    if (!onOpen) return;
    if (e.target.closest('button, a, input, select, label, [role="tab"]')) return;
    onOpen();
  };
}

function Shell({ title, subtitle, state, empty, children, bodyClassName, onOpen }) {
  const body = state.requiresPro ? <ProNote /> : (empty ? <NoAccounts /> : children);
  const clickable = Boolean(onOpen && !state.requiresPro && !empty && !state.loading && !state.error);
  return (
    <WidgetShell
      title={title}
      subtitle={subtitle}
      loading={state.loading}
      error={state.requiresPro ? null : state.error}
      onRetry={state.retry}
      bodyClassName={bodyClassName}
    >
      <div
        className={`mydw-body${state.refreshing ? ' is-refreshing' : ''}${clickable ? ' is-clickable' : ''}`}
        onClick={clickable ? openUnlessControl(onOpen) : undefined}
        onKeyDown={clickable ? (e) => { if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(); } : undefined}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-label={clickable ? `${title}: view details` : undefined}
      >
        {body}
        {clickable ? <span className="mydw-details-hint" aria-hidden="true">View details ↗</span> : null}
      </div>
    </WidgetShell>
  );
}

function Pills({ options, value, onChange, label }) {
  return (
    <div className="mydw-pills" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value} className={value === o.value ? 'active' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const SHORT_RANGES = [
  { value: '1M', label: '1M' }, { value: '3M', label: '3M' }, { value: '6M', label: '6M' },
  { value: 'YTD', label: 'YTD' }, { value: '1Y', label: '1Y' }, { value: 'All', label: 'All' },
];

/* Equity curve ---------------------------------------------------------- */

function EquityTooltip({ active, payload, label, stacked }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="mydw-tip">
      <span className="mydw-tip-date">{niceDate(label)}</span>
      {stacked ? (
        <>
          {payload.slice().reverse().map((p) => (
            <span key={p.dataKey} className="mydw-tip-row"><i style={{ background: p.color }} />{p.name}<b>{usd(p.value)}</b></span>
          ))}
          <span className="mydw-tip-row is-total">Total<b>{usd(row.total)}</b></span>
        </>
      ) : (
        <>
          <b className="mydw-tip-value">{usd(row.value, { cents: true })}</b>
          {row.change != null ? <span className={`mydw-tip-change ${tone(row.change)}`}>{usd(row.change, { sign: true, cents: true })} that day</span> : null}
        </>
      )}
    </div>
  );
}

export function MyEquityWidget({ config, onConfigChange }) {
  const { account = ALL_ACCOUNTS } = config || {};
  const [range, setRange] = useWidgetSetting(config, 'range', '3M', onConfigChange);
  const [mode, setMode] = useState('total');
  const [open, setOpen] = useState(false);
  const state = useMyData(() => getMyPerformance(account, range), [account, range]);
  const points = useMemo(() => state.data?.points || [], [state.data]);
  const perAccount = useMemo(() => state.data?.perAccount || [], [state.data]);
  const s = state.data?.summary;

  const totalRows = useMemo(() => points.map((p, i) => ({
    date: p.date,
    value: Number(p.value) || 0,
    change: i ? (Number(p.value) || 0) - (Number(points[i - 1].value) || 0) - (Number(p.netFlow) || 0) : null,
  })), [points]);

  // Accounts side by side: each carried forward to every date, stacked.
  const stacked = useMemo(() => {
    if (perAccount.length < 2) return null;
    const dates = [...new Set(perAccount.flatMap((a) => a.points.map((p) => p.date)))].sort();
    const maps = perAccount.map((a) => new Map(a.points.map((p) => [p.date, Number(p.value) || 0])));
    const last = perAccount.map(() => 0);
    const rows = dates.map((date) => {
      const row = { date, total: 0 };
      maps.forEach((m, i) => {
        if (m.has(date)) last[i] = m.get(date);
        row[`a${i}`] = Math.max(0, last[i]);
        row.total += last[i];
      });
      return row;
    });
    const series = perAccount.map((a, i) => ({
      key: `a${i}`,
      name: cleanAccountName(a.name),
      broker: a.broker,
      color: brandFor(a.broker, a.name)?.color || PALETTE[i % PALETTE.length],
    }));
    return { rows, series };
  }, [perAccount]);

  const change = s ? s.equity - s.startEquity - points.reduce((sum, p, i) => sum + (i ? Number(p.netFlow) || 0 : 0), 0) : 0;
  const changePct = s?.startEquity > 0 ? change / s.startEquity : null;
  const up = change >= 0;
  const value = useCountUp(s?.equity ?? 0);
  const peak = totalRows.length ? totalRows.reduce((a, b) => (b.value > a.value ? b : a)) : null;
  const showStacked = mode === 'accounts' && stacked;

  return (
    <Shell
      title="My equity"
      subtitle={<SourceLine choice={account} missing={state.data?.missing} onClick={() => setOpen(true)} />}
      state={state}
      empty={state.data && !state.data.accounts.length}
      bodyClassName="dashboard-widget-body-chart"
      onOpen={() => setOpen(true)}
    >
      {open ? (
        <InsightModal title="Equity by account" subtitle={`${describeAccountChoice(account)} · ${rangeLabel(range)}`} onClose={() => setOpen(false)} wide>
          <AccountBreakdown perf={state.data} range={range} highlight="equity" />
        </InsightModal>
      ) : null}
      <div className="mydw-eq">
        <div className="mydw-eq-head">
          <div>
            <div className="mydw-eq-value">{usd(value)}</div>
            <span className={`mydw-chip ${up ? 'is-up' : 'is-down'}`}>
              {up ? '▲' : '▼'} {usd(change, { sign: true })}{changePct != null ? ` (${pct(changePct)})` : ''}
              <em> {rangeLabel(range)}</em>
            </span>
          </div>
          <div className="mydw-eq-controls">
            {stacked ? <Pills label="View" value={mode} onChange={setMode} options={[{ value: 'total', label: 'Total' }, { value: 'accounts', label: 'By account' }]} /> : null}
            <Pills label="Period" value={range} onChange={setRange} options={SHORT_RANGES} />
          </div>
        </div>
        <div className="mydw-eq-chart">
          {points.length < 2 ? <div className="mydw-state"><span>Not enough history yet. It fills in as your accounts sync.</span></div> : (
            <ResponsiveContainer width="100%" height="100%">
              {showStacked ? (
                <AreaChart data={stacked.rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} minTickGap={32} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => usd(v, { compact: true })} tick={{ fontSize: 11 }} width={64} axisLine={false} tickLine={false} />
                  <Tooltip content={<EquityTooltip stacked />} />
                  {stacked.series.map((ser) => (
                    <Area key={ser.key} type="monotone" dataKey={ser.key} name={ser.name} stackId="1" stroke={ser.color} fill={ser.color} fillOpacity={0.35} strokeWidth={1.5} />
                  ))}
                </AreaChart>
              ) : (
                <AreaChart data={totalRows} margin={{ top: 14, right: 12, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id={`mydwEq${up ? 'Up' : 'Dn'}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={up ? UP : DOWN} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={up ? UP : DOWN} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} minTickGap={32} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(v) => usd(v, { compact: true })} tick={{ fontSize: 11 }} width={64} domain={['auto', 'auto']} axisLine={false} tickLine={false} />
                  <Tooltip content={<EquityTooltip />} />
                  <Area type="monotone" dataKey="value" name="Equity" stroke={up ? UP : DOWN} fill={`url(#mydwEq${up ? 'Up' : 'Dn'})`} strokeWidth={2} />
                  {peak ? <ReferenceDot x={peak.date} y={peak.value} r={4} fill="#fff" stroke={up ? UP : DOWN} strokeWidth={2} label={{ value: `Peak ${usd(peak.value)}`, position: 'top', fontSize: 11, fill: 'currentColor' }} /> : null}
                </AreaChart>
              )}
            </ResponsiveContainer>
          )}
        </div>
        {showStacked ? (
          <div className="mydw-legend">
            {stacked.series.map((ser) => (
              <span key={ser.key}><i style={{ background: ser.color }} /><BrokerMark broker={ser.broker} name={ser.name} size={14} />{ser.name}</span>
            ))}
          </div>
        ) : null}
      </div>
    </Shell>
  );
}

/* KPI card ----------------------------------------------------------------- */

export const MY_KPI_OPTIONS = [
  { value: 'netPnl', label: 'Net P&L' },
  { value: 'todayPnl', label: 'Today’s P&L' },
  { value: 'equity', label: 'Account value' },
  { value: 'winDayRate', label: 'Winning days %' },
  { value: 'maxDrawdown', label: 'Max drawdown' },
  { value: 'bestDay', label: 'Best day' },
  { value: 'worstDay', label: 'Worst day' },
  { value: 'profitFactor', label: 'Profit factor' },
  { value: 'tradingDays', label: 'Trading days' },
];

function Sparkline({ data, dataKey = 'v', color }) {
  const idRef = useRef(`mydwSp${Math.random().toString(36).slice(2, 8)}`);
  const id = idRef.current;
  if (!data || data.length < 2) return null;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <YAxis hide domain={['auto', 'auto']} />
        <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#${id})`} strokeWidth={2} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function DayBars({ daily, highlight }) {
  const data = daily.slice(-24).map((d) => ({ date: d.date, pnl: d.pnl }));
  if (!data.length) return null;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
        <YAxis hide />
        <Bar dataKey="pnl" radius={[2, 2, 2, 2]} isAnimationActive={false}>
          {data.map((d) => (
            <Cell key={d.date} fill={d.pnl >= 0 ? UP : DOWN} fillOpacity={highlight && d.date === highlight ? 1 : 0.55} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function SplitBar({ left, right, leftLabel, rightLabel }) {
  const total = Math.abs(left) + Math.abs(right) || 1;
  return (
    <div className="mydw-split">
      <div className="mydw-split-bar">
        <span className="is-up" style={{ width: `${(Math.abs(left) / total) * 100}%` }} />
        <span className="is-down" style={{ width: `${(Math.abs(right) / total) * 100}%` }} />
      </div>
      <div className="mydw-split-labels"><span className="is-up">{leftLabel}</span><span className="is-down">{rightLabel}</span></div>
    </div>
  );
}

export function MyKpiWidget({ config }) {
  const { account = ALL_ACCOUNTS, range = '3M', metric = 'netPnl' } = config || {};
  const state = useMyData(() => getMyPerformance(account, range), [account, range]);
  const s = state.data?.summary;
  const points = state.data?.points || [];
  const daily = state.data?.daily || [];
  const label = MY_KPI_OPTIONS.find((o) => o.value === metric)?.label || 'Net P&L';
  const [open, setOpen] = useState(false);
  // Weekends and holidays: "Today's P&L" shows the last trading day (labelled in the chip).
  const raw = s ? (metric === 'todayPnl' ? (s.todayPnl || s.lastDayPnl) : s[metric]) : null;
  const animated = useCountUp(Number.isFinite(raw) ? raw : 0);

  let value = '—';
  let cls = '';
  let chip = null;
  let visual = null;
  if (s) {
    const v = animated;
    if (metric === 'winDayRate') value = `${Math.round((v || 0) * 100)}%`;
    else if (metric === 'profitFactor') value = Number.isFinite(raw) ? (v || 0).toFixed(2) : '∞';
    else if (metric === 'tradingDays') value = String(Math.round(v || 0));
    else if (metric === 'equity') value = usd(v);
    else { value = usd(v, { sign: true }); cls = tone(raw); }

    const equityLine = points.map((p) => ({ v: Number(p.value) || 0 }));
    let running = 0;
    const pnlLine = daily.map((d) => { running += d.pnl; return { v: running }; });
    let peak = -Infinity;
    const ddLine = pnlLine.map((p) => { peak = Math.max(peak, p.v); return { v: p.v - peak }; });

    switch (metric) {
      case 'equity': {
        const delta = s.equity - s.startEquity;
        chip = <span className={`mydw-chip ${tone(delta)}`}>{usd(delta, { sign: true })}{s.startEquity > 0 ? ` (${pct(delta / s.startEquity)})` : ''} <em>{rangeLabel(range)}</em></span>;
        visual = <Sparkline data={equityLine} color={delta >= 0 ? UP : DOWN} />;
        break;
      }
      case 'netPnl':
        chip = <span className={`mydw-chip ${tone(s.netPnl)}`}>{s.returnPct != null ? `${pct(s.returnPct)} return` : 'Net of deposits'} <em>{rangeLabel(range)}</em></span>;
        visual = <Sparkline data={pnlLine} color={s.netPnl >= 0 ? UP : DOWN} />;
        break;
      case 'todayPnl': {
        const shown = s.todayPnl || s.lastDayPnl;
        const when = s.todayPnl ? 'today' : (s.lastDay ? niceDate(s.lastDay) : '');
        chip = <span className={`mydw-chip ${tone(shown)}`}>{s.equity > 0 ? `${pct(shown / s.equity)} of account` : ''} <em>{when}</em></span>;
        visual = <DayBars daily={daily} highlight={s.lastDay} />;
        break;
      }
      case 'winDayRate':
        chip = <span className="mydw-chip">{s.winDays} up · {s.lossDays} down days</span>;
        visual = <SplitBar left={s.winDays} right={s.lossDays} leftLabel={`${s.winDays} green`} rightLabel={`${s.lossDays} red`} />;
        break;
      case 'profitFactor':
        chip = <span className={`mydw-chip ${raw >= 1 ? 'is-up' : 'is-down'}`}>{raw >= 1 ? 'Winning more than losing' : 'Losing more than winning'}</span>;
        visual = <SplitBar left={s.grossWin} right={s.grossLoss} leftLabel={`${usd(s.grossWin)} won`} rightLabel={`${usd(s.grossLoss)} lost`} />;
        break;
      case 'maxDrawdown':
        chip = <span className="mydw-chip is-down">{s.peakEquity > 0 ? `${pct(s.maxDrawdown / s.peakEquity)} from peak` : 'From the high point'}</span>;
        visual = <Sparkline data={ddLine} color={DOWN} />;
        break;
      case 'bestDay':
        chip = <span className="mydw-chip is-up">{s.bestDate ? niceDate(s.bestDate) : ''}</span>;
        visual = <DayBars daily={daily} highlight={s.bestDate} />;
        break;
      case 'worstDay':
        chip = <span className="mydw-chip is-down">{s.worstDate ? niceDate(s.worstDate) : ''}</span>;
        visual = <DayBars daily={daily} highlight={s.worstDate} />;
        break;
      case 'tradingDays':
        chip = <span className="mydw-chip">{rangeLabel(range)}</span>;
        visual = <SplitBar left={s.winDays} right={s.lossDays} leftLabel={`${s.winDays} up`} rightLabel={`${s.lossDays} down`} />;
        break;
      default:
        break;
    }
  }

  return (
    <Shell title={label} subtitle={<SourceLine choice={account} missing={state.data?.missing} onClick={() => setOpen(true)} />} state={state} empty={state.data && !state.data.accounts.length} onOpen={() => setOpen(true)}>
      {open ? (
        <InsightModal title={`${label} by account`} subtitle={`${describeAccountChoice(account)} · ${rangeLabel(range)}`} onClose={() => setOpen(false)} wide>
          <AccountBreakdown perf={state.data} range={range} highlight={metric} />
        </InsightModal>
      ) : null}
      <div className="mydw-kpi-card">
        <div className={`mydw-kpi ${cls}`}>{value}</div>
        {chip}
        {visual ? <div className="mydw-kpi-visual">{visual}</div> : null}
      </div>
    </Shell>
  );
}

/* Daily P&L calendar -------------------------------------------------------- */

// Daily changes under 5¢ are rounding between balance snapshots, not P&L.
const FLAT = 0.05;
const meaningful = (v) => v != null && Math.abs(v) >= FLAT;

/** One account's change on a date: value vs the previous day, minus money moved in or out. */
function accountChangeOn(points, iso) {
  const i = points.findIndex((p) => p.date === iso);
  if (i <= 0) return null;
  return (Number(points[i].value) || 0) - (Number(points[i - 1].value) || 0) - (Number(points[i].netFlow) || 0);
}

function DayDetail({ iso, perf, trades, activeDays, onClose, onMove }) {
  const dialogRef = useRef(null);
  const idx = activeDays.indexOf(iso);
  const prevDay = idx > 0 ? activeDays[idx - 1] : null;
  const nextDay = idx >= 0 && idx < activeDays.length - 1 ? activeDays[idx + 1] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && prevDay) onMove(prevDay);
      else if (e.key === 'ArrowRight' && nextDay) onMove(nextDay);
    };
    window.addEventListener('keydown', onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onMove, prevDay, nextDay]);

  const day = (perf?.daily || []).find((d) => d.date === iso);
  const pnl = day && meaningful(day.pnl) ? day.pnl : 0;
  const point = (perf?.points || []).find((p) => p.date === iso);
  const close = point ? Number(point.value) || 0 : null;
  const open = close != null ? close - pnl - (Number(point?.netFlow) || 0) : null;
  const flow = Number(point?.netFlow) || 0;
  const accounts = (perf?.perAccount || [])
    .map((a) => ({ ...a, change: accountChangeOn(a.points, iso), value: a.points.find((p) => p.date === iso)?.value }))
    .filter((a) => meaningful(a.change) || a.value != null)
    .sort((a, b) => Math.abs(b.change || 0) - Math.abs(a.change || 0));
  const maxAcct = Math.max(1, ...accounts.map((a) => Math.abs(a.change || 0)));
  const dayTrades = collapseStrategies(trades.filter((t) => String(t.exitDate || '').slice(0, 10) === iso));
  const realized = dayTrades.reduce((sum, t) => sum + (Number(t.profit) || 0), 0);
  const dayWins = dayTrades.filter((t) => Number(t.profit) > 0).length;
  const when = new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return createPortal(
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="mydw-day-backdrop" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="mydw-day" role="dialog" aria-modal="true" aria-label={`P&L for ${when}`} tabIndex={-1} ref={dialogRef}>
        <header className="mydw-day-head">
          <button type="button" onClick={() => prevDay && onMove(prevDay)} disabled={!prevDay} aria-label="Previous day with activity">‹</button>
          <div>
            <span className="mydw-day-when">{when}</span>
            <strong className={`mydw-day-pnl ${tone(pnl)}`}>{meaningful(pnl) ? usd(pnl, { sign: true, cents: true }) : 'No change'}</strong>
            {open > 0 && meaningful(pnl) ? <span className={`mydw-chip ${tone(pnl)}`}>{pct(pnl / open)} on the day</span> : null}
          </div>
          <button type="button" onClick={() => nextDay && onMove(nextDay)} disabled={!nextDay} aria-label="Next day with activity">›</button>
          <button type="button" className="mydw-day-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="mydw-day-stats">
          <div><span>Value at close</span><b>{close != null ? usd(close, { cents: true }) : '—'}</b></div>
          <div><span>Closed trades</span><b>{dayTrades.length ? `${dayTrades.length} · ${dayWins} won` : 'None'}</b></div>
          <div><span>Realized</span><b className={tone(realized)}>{dayTrades.length ? usd(realized, { sign: true, cents: true }) : '—'}</b></div>
          {meaningful(flow) ? <div><span>{flow > 0 ? 'Deposited' : 'Withdrawn'}</span><b>{usd(Math.abs(flow), { cents: true })}</b></div> : null}
        </div>

        {accounts.length > 1 || (accounts.length === 1 && meaningful(accounts[0].change)) ? (
          <section className="mydw-day-sec">
            <h4>By account</h4>
            <ul className="mydw-day-accts">
              {accounts.map((a) => (
                <li key={a.id}>
                  <BrokerMark broker={a.broker} name={a.name} size={22} />
                  <span className="mydw-day-acct-name">
                    <strong>{cleanAccountName(a.name)}</strong>
                    <span className="mydw-day-acct-bar"><i className={tone(a.change)} style={{ width: `${(Math.abs(a.change || 0) / maxAcct) * 100}%` }} /></span>
                  </span>
                  <span className="mydw-alloc-num">
                    <b className={tone(a.change)}>{meaningful(a.change) ? usd(a.change, { sign: true, cents: true }) : '—'}</b>
                    <small>{a.value != null ? usd(a.value) : ''}</small>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="mydw-day-sec">
          <h4>Trades closed</h4>
          {dayTrades.length ? (
            <ul className="mydw-trade-list mydw-day-trades">
              {dayTrades.map((t, i) => {
                const p = Number(t.profit) || 0;
                return (
                  <li key={`${t.symbol}-${i}`}>
                    <BrokerMark broker={t.broker} name={t.account} size={22} />
                    <span className="mydw-trade-main">
                      <strong>{t.symbol}{t.quantity != null ? <small className="mydw-qty"> × {Number(t.quantity).toLocaleString('en-US', { maximumFractionDigits: 4 })}</small> : null}</strong>
                      <small>
                        {cleanAccountName(t.account)}
                        {t.legsText ? ` · ${t.legsText}` : ''}
                        {t.entryPrice != null && t.exitPrice != null ? ` · ${usd(t.entryPrice, { cents: true })} → ${usd(t.exitPrice, { cents: true })}` : ''}
                        {t.entryDate && t.entryDate !== t.exitDate ? ` · opened ${shortDate(String(t.entryDate).slice(0, 10))}` : ''}
                      </small>
                    </span>
                    {sideTagText(t) ? <span className={`mydw-side ${sideTagClass(t)}`}>{sideTagText(t)}</span> : null}
                    <b className={`mydw-day-trade-pnl ${tone(p)}`}>{usd(p, { sign: true, cents: true })}</b>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mydw-day-empty">
              {meaningful(pnl)
                ? 'No trades closed this day. The change came from price moves in positions you were holding.'
                : 'No trades and no change in value this day.'}
            </p>
          )}
        </section>
        <p className="mydw-day-hint">Use ← → to step through days with activity · Esc to close</p>
      </div>
    </div>,
    document.body,
  );
}

export function MyPnlCalendarWidget({ config }) {
  const { account = ALL_ACCOUNTS } = config || {};
  const state = useMyData(async () => {
    const [perf, trades] = await Promise.all([
      getMyPerformance(account, '1Y'),
      getMyTrades(account, '1Y').catch(() => []),
    ]);
    return { ...perf, trades };
  }, [account]);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(null);
  const trades = useMemo(() => state.data?.trades || [], [state.data]);
  const byDate = useMemo(() => new Map((state.data?.daily || []).map((d) => [d.date, d.pnl])), [state.data]);
  const tradesByDate = useMemo(() => {
    const m = new Map();
    trades.forEach((t) => {
      const d = String(t.exitDate || '').slice(0, 10);
      if (d) m.set(d, (m.get(d) || 0) + 1);
    });
    return m;
  }, [trades]);
  // Days worth stepping through in the detail view.
  const activeDays = useMemo(() => [...new Set([
    ...[...byDate.entries()].filter(([, v]) => meaningful(v)).map(([d]) => d),
    ...tradesByDate.keys(),
  ])].sort(), [byDate, tradesByDate]);

  const now = new Date();
  const month = new Date(now.getFullYear(), now.getMonth() - offset, 1);
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const lead = month.getDay();
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= days; d += 1) {
    const iso = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const v = byDate.get(iso);
    cells.push({ d, iso, pnl: meaningful(v) ? v : null, trades: tradesByDate.get(iso) || 0 });
  }
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  const inMonth = cells.filter((c) => c && c.pnl != null);
  const monthTotal = inMonth.reduce((s, c) => s + c.pnl, 0);
  const maxAbs = Math.max(1, ...inMonth.map((c) => Math.abs(c.pnl)));
  const green = inMonth.filter((c) => c.pnl > 0).length;
  const red = inMonth.filter((c) => c.pnl < 0).length;
  const best = inMonth.length ? inMonth.reduce((a, b) => (b.pnl > a.pnl ? b : a)) : null;
  const worst = inMonth.length ? inMonth.reduce((a, b) => (b.pnl < a.pnl ? b : a)) : null;
  const monthTrades = cells.reduce((s, c) => s + (c?.trades || 0), 0);
  const todayIso = now.toISOString().slice(0, 10);

  const jump = (iso) => {
    setSelected(iso);
    const d = new Date(`${iso}T12:00:00`);
    setOffset((now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth()));
  };

  return (
    <Shell title="My P&L calendar" subtitle={<SourceLine choice={account} missing={state.data?.missing} extra="click a day for details" />} state={state} empty={state.data && !state.data.accounts.length}>
      <div className="mydw-cal">
        <div className="mydw-cal-head">
          <button type="button" onClick={() => setOffset((o) => Math.min(11, o + 1))} disabled={offset >= 11} aria-label="Previous month">‹</button>
          <span className="mydw-cal-title">
            {month.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
            <b className={tone(monthTotal)}>{usd(monthTotal, { sign: true })}</b>
          </span>
          <button type="button" onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset === 0} aria-label="Next month">›</button>
        </div>
        <div className="mydw-cal-grid" style={{ gridTemplateRows: `auto repeat(${weeks.length}, minmax(0, 1fr))` }}>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((w, i) => <span key={`h${i}`} className="mydw-cal-dow">{w}</span>)}
          <span className="mydw-cal-dow">Week</span>
          {weeks.map((week, wi) => {
            const withData = week.filter((c) => c && c.pnl != null);
            const wk = withData.reduce((s, c) => s + c.pnl, 0);
            return (
              <React.Fragment key={`w${wi}`}>
                {week.map((c, i) => (c ? (
                  <button
                    type="button"
                    key={`d${wi}-${i}`}
                    className={`mydw-cal-day ${c.pnl != null ? tone(c.pnl) : ''}${c.iso === todayIso ? ' is-today' : ''}${selected === c.iso ? ' is-selected' : ''}${c.iso > todayIso ? ' is-future' : ''}`}
                    style={c.pnl != null ? { '--mydw-int': 0.15 + 0.6 * (Math.abs(c.pnl) / maxAbs) } : undefined}
                    onClick={() => setSelected(c.iso)}
                    disabled={c.iso > todayIso}
                    aria-label={`${niceDate(c.iso)}${c.pnl != null ? `, ${usd(c.pnl, { sign: true, cents: true })}` : ''}${c.trades ? `, ${c.trades} trades` : ''}`}
                  >
                    <span className="mydw-cal-day-top">
                      <i>{c.d}</i>
                      {c.trades ? <span className="mydw-cal-trades" title={`${c.trades} trade${c.trades === 1 ? '' : 's'} closed`}>{c.trades}</span> : null}
                    </span>
                    {c.pnl != null ? <em>{usd(c.pnl, { sign: true, compact: true })}</em> : null}
                  </button>
                ) : <span key={`e${wi}-${i}`} className="mydw-cal-empty" />))}
                <span className={`mydw-cal-week ${withData.length ? tone(wk) : ''}`}>
                  {withData.length ? usd(wk, { sign: true, compact: true }) : ''}
                </span>
              </React.Fragment>
            );
          })}
        </div>
        <div className="mydw-cal-foot">
          <span><i className="is-up" />{green} green</span>
          <span><i className="is-down" />{red} red</span>
          {monthTrades ? <span>{monthTrades} trades closed</span> : null}
          {best && best.pnl > 0 ? <button type="button" className="mydw-cal-link" onClick={() => jump(best.iso)}>Best {usd(best.pnl, { sign: true })} · {shortDate(best.iso)}</button> : null}
          {worst && worst.pnl < 0 ? <button type="button" className="mydw-cal-link" onClick={() => jump(worst.iso)}>Worst {usd(worst.pnl, { sign: true })} · {shortDate(worst.iso)}</button> : null}
        </div>
      </div>
      {selected ? (
        <DayDetail
          iso={selected}
          perf={state.data}
          trades={trades}
          activeDays={activeDays}
          onClose={() => setSelected(null)}
          onMove={jump}
        />
      ) : null}
    </Shell>
  );
}

/* Trades --------------------------------------------------------------------- */

export function MyTradesWidget({ config }) {
  // The latest closed trades, however long ago (older dashboards may still carry a range; it's ignored).
  const { account = ALL_ACCOUNTS } = config || {};
  const state = useMyData(() => getMyTrades(account, 'All'), [account]);
  const [open, setOpen] = useState(false);
  // Option legs placed together count as one trade (a call spread, not two).
  const all = useMemo(() => collapseStrategies(state.data || []), [state.data]);
  const trades = all.slice(0, 50);
  const total = all.reduce((s, t) => s + (Number(t.profit) || 0), 0);
  const wins = all.filter((t) => Number(t.profit) > 0).length;
  const maxAbs = Math.max(1, ...trades.map((t) => Math.abs(Number(t.profit) || 0)));
  return (
    <Shell title="My recent trades" subtitle={<SourceLine choice={account} extra="most recent first" onClick={all.length ? () => setOpen(true) : undefined} />} state={state} onOpen={all.length ? () => setOpen(true) : undefined}>
      {open ? (
        <InsightModal title="All your trades" subtitle={describeAccountChoice(account)} onClose={() => setOpen(false)} wide>
          <TradesInsight trades={all} />
        </InsightModal>
      ) : null}
      {!trades.length ? <div className="mydw-state"><span>No closed trades yet.</span></div> : (
        <div className="mydw-trades">
          <div className="mydw-trades-sum">
            <span><b className={tone(total)}>{usd(total, { sign: true })}</b> realized, all time</span>
            <span><b>{all.length}</b> trades</span>
            <span><b>{Math.round((wins / all.length) * 100)}%</b> winners</span>
          </div>
          <ul className="mydw-trade-list">
            {trades.map((t, i) => {
              const p = Number(t.profit) || 0;
              return (
                <li key={`${t.symbol}-${t.exitDate}-${i}`}>
                  <BrokerMark broker={t.broker} name={t.account} size={22} />
                  <span className="mydw-trade-main">
                    <strong>{t.symbol}</strong>
                    <small>{cleanAccountName(t.account)} · {shortDate(String(t.exitDate || t.entryDate || '').slice(0, 10))}{t.legsText ? ` · ${t.legsText}` : ''}</small>
                  </span>
                  {sideTagText(t) ? <span className={`mydw-side ${sideTagClass(t)}`}>{sideTagText(t)}</span> : null}
                  <span className="mydw-trade-pnl">
                    <b className={tone(p)}>{usd(p, { sign: true, cents: true })}</b>
                    <i className={tone(p)} style={{ width: `${Math.max(6, (Math.abs(p) / maxAbs) * 100)}%` }} />
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Shell>
  );
}

/* Insights ------------------------------------------------------------------- */

export function MyInsightsWidget() {
  return (
    <WidgetShell title="Insights from my trades">
      <TradeInsightsPanel compact />
    </WidgetShell>
  );
}

/* Open positions ------------------------------------------------------------- */

export function MyPositionsWidget({ config }) {
  const { account = ALL_ACCOUNTS } = config || {};
  const state = useMyData(() => getMyPositions(account), [account]);
  const [open, setOpen] = useState(false);
  const positions = useMemo(() => collapseStrategies(state.data?.positions || []), [state.data]);
  const unsupported = state.data?.unsupported || [];
  const totalValue = positions.reduce((s, p) => s + (Number(p.marketValue) || 0), 0);
  const totalUpl = positions.reduce((s, p) => s + (Number(p.unrealizedPl ?? p.unrealizedPnl ?? p.unrealized) || 0), 0);
  return (
    <Shell title="My open positions" subtitle={<SourceLine choice={account} onClick={positions.length ? () => setOpen(true) : undefined} />} state={state} onOpen={positions.length ? () => setOpen(true) : undefined}>
      {open ? (
        <InsightModal title="Your open positions" subtitle={describeAccountChoice(account)} onClose={() => setOpen(false)} wide>
          <PositionsInsight positions={positions} />
        </InsightModal>
      ) : null}
      {!positions.length ? <div className="mydw-state"><span>No open positions.</span></div> : (
        <div className="mydw-trades">
          <div className="mydw-trades-sum">
            <span><b>{usd(totalValue)}</b> held</span>
            <span><b className={tone(totalUpl)}>{usd(totalUpl, { sign: true })}</b> unrealized</span>
            <span><b>{positions.length}</b> positions</span>
          </div>
          <ul className="mydw-trade-list">
            {positions.map((p, i) => {
              const upl = p.unrealizedPl ?? p.unrealizedPnl ?? p.unrealized ?? null;
              const uplPct = p.unrealizedPlpc != null ? Number(p.unrealizedPlpc) : null;
              return (
                <li key={`${p.symbol}-${i}`}>
                  <BrokerMark broker={p.broker} name={p.account} size={22} />
                  <span className="mydw-trade-main">
                    <strong>{p.symbol}</strong>
                    <small>{cleanAccountName(p.account)} · {p.isStrategy ? `${p.sideLabel} · ${p.legsText}` : `${p.quantity ?? p.qty ?? '—'} @ ${p.currentPrice != null ? usd(p.currentPrice, { cents: true }) : '—'}`}</small>
                  </span>
                  <span className="mydw-pos-val">
                    <b>{p.marketValue != null ? usd(p.marketValue) : '—'}</b>
                    <small className={tone(upl)}>{upl != null ? usd(upl, { sign: true }) : ''}{uplPct != null && Number.isFinite(uplPct) ? ` (${pct(uplPct)})` : ''}</small>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {unsupported.length ? <p className="mydw-foot">Per-symbol positions aren’t available for {unsupported.map(cleanAccountName).join(', ')}.</p> : null}
    </Shell>
  );
}

/* Allocation ------------------------------------------------------------------ */

export function MyAllocationWidget({ config, onConfigChange }) {
  const [groupBy, setGroupBy] = useWidgetSetting(config, 'groupBy', 'broker', onConfigChange);
  const state = useMyData(() => getMyAccountBalances(), []);
  const [hover, setHover] = useState(null);
  const [open, setOpen] = useState(false);
  const data = useMemo(() => {
    const rows = state.data || [];
    const map = new Map();
    rows.forEach((r) => {
      const key = groupBy === 'account' ? cleanAccountName(r.name) : brokerLabel(r.broker);
      const prev = map.get(key) || { name: key, value: 0, broker: r.broker, accountName: r.name, count: 0 };
      prev.value += Math.max(0, r.equity);
      prev.count += 1;
      map.set(key, prev);
    });
    return [...map.values()].filter((d) => d.value > 0).sort((a, b) => b.value - a.value)
      .map((d, i) => ({ ...d, color: (groupBy === 'broker' && brandFor(d.broker, d.accountName)?.color) || PALETTE[i % PALETTE.length] }));
  }, [state.data, groupBy]);
  const total = data.reduce((s, d) => s + d.value, 0);
  const focus = hover != null ? data[hover] : null;
  return (
    <Shell
      title="My allocation"
      subtitle={<SourceLine choice={ALL_ACCOUNTS} extra={groupBy === 'account' ? '' : `${data.length} brokers`} onClick={() => setOpen(true)} />}
      state={state}
      empty={state.data && !state.data.length}
      bodyClassName="dashboard-widget-body-chart"
      onOpen={() => setOpen(true)}
    >
      {open ? (
        <InsightModal title="What you hold" subtitle="Every account, by symbol" onClose={() => setOpen(false)} wide>
          <HoldingsInsight />
        </InsightModal>
      ) : null}
      {!data.length ? <div className="mydw-state"><span>No balances yet.</span></div> : (
        <div className="mydw-alloc">
          <div className="mydw-alloc-top">
            <Pills label="Group by" value={groupBy} onChange={setGroupBy} options={[{ value: 'broker', label: 'By broker' }, { value: 'account', label: 'By account' }]} />
          </div>
          <div className="mydw-alloc-main">
            <div className="mydw-alloc-donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="64%"
                    outerRadius="92%"
                    paddingAngle={data.length > 1 ? 2 : 0}
                    stroke="none"
                    onMouseEnter={(_, i) => setHover(i)}
                    onMouseLeave={() => setHover(null)}
                  >
                    {data.map((d, i) => <Cell key={d.name} fill={d.color} fillOpacity={hover == null || hover === i ? 1 : 0.35} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="mydw-alloc-center">
                <b>{usd(focus ? focus.value : total)}</b>
                <span>{focus ? `${focus.name} · ${Math.round((focus.value / total) * 100)}%` : 'Total'}</span>
              </div>
            </div>
            <ul className="mydw-alloc-list">
              {data.map((d, i) => (
                <li key={d.name} className={hover === i ? 'is-hover' : ''} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                  <BrokerMark broker={d.broker} name={d.accountName} size={22} />
                  <span className="mydw-alloc-name">
                    <strong>{d.name}</strong>
                    <span className="mydw-alloc-bar"><i style={{ width: `${(d.value / total) * 100}%`, background: d.color }} /></span>
                  </span>
                  <span className="mydw-alloc-num">
                    <b>{usd(d.value)}</b>
                    <small>{((d.value / total) * 100).toFixed(1)}%</small>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Shell>
  );
}

/* Live accounts (Trade Watcher) --------------------------------------------- */

export function MyLiveAccountsWidget({ user }) {
  const state = useMyData(() => getMyLiveAccounts(user?.email), [user?.email]);
  const rows = state.data || [];
  const day = rows.reduce((s, r) => s + (Number(r.dayPnl) || 0), 0);
  return (
    <Shell title="Live accounts" subtitle={rows.length ? `NinjaTrader · today ${usd(day, { sign: true })}` : 'NinjaTrader via the RiskLo Tally add-on'} state={state}>
      {!rows.length ? <div className="mydw-state"><span>Connect NinjaTrader in Trade Watcher to see your accounts live.</span></div> : (
        <ul className="mydw-live">
          {rows.slice(0, 20).map((r) => (
            <li key={`${r.feedId}-${r.ntAccount}`}>
              <span className={`mydw-dot ${r.connected === 'Yes' ? 'is-on' : ''}`} aria-hidden="true" />
              <span className="mydw-live-name">{r.ntAccount}{r.naked === 'Yes' ? <b className="mydw-naked">Naked</b> : null}</span>
              <span className={`num ${tone(r.dayPnl)}`}>{usd(r.dayPnl, { sign: true })}</span>
            </li>
          ))}
        </ul>
      )}
    </Shell>
  );
}

/* My account vs a company strategy -------------------------------------------- */

export function MyVsStrategyWidget({ config, user, customCombos }) {
  const { account = ALL_ACCOUNTS, range = '3M', strategy = '', contractType = 'NQ', contractSize = 1 } = config || {};
  const state = useMyData(async () => {
    const mine = await getMyPerformance(account, range);
    let strat = [];
    if (strategy) {
      const sheet = await getStrategySheet({ strategy, email: user?.email }).catch(() => null);
      strat = buildChartData({
        weeklyData: extractWeeklyData(Array.isArray(sheet?.rows) ? sheet.rows : []),
        contractType,
        contractSize: Number(contractSize) || 1,
        sheetName: strategy,
      });
    }
    return { mine, strat };
  }, [account, range, strategy, contractType, contractSize]);

  const data = useMemo(() => {
    const daily = state.data?.mine?.daily || [];
    if (!daily.length) return [];
    const start = daily[0].date;
    const stratDaily = new Map((state.data?.strat || []).filter((p) => p.date >= start).map((p) => [p.date, p.dailyPnl]));
    let mine = 0;
    let theirs = 0;
    const dates = [...new Set([...daily.map((d) => d.date), ...stratDaily.keys()])].sort();
    const mineByDate = new Map(daily.map((d) => [d.date, d.pnl]));
    return dates.map((date) => {
      mine += mineByDate.get(date) || 0;
      theirs += stratDaily.get(date) || 0;
      return { date, mine: Math.round(mine), strategy: Math.round(theirs) };
    });
  }, [state.data]);

  const stratName = strategy ? resolveStrategyDisplayName(strategy, customCombos) : '';
  return (
    <Shell
      title="My account vs strategy"
      subtitle={`${describeAccountChoice(account)}${stratName ? ` vs ${stratName}` : ''}`}
      state={state}
      empty={state.data && !state.data.mine.accounts.length}
      bodyClassName="dashboard-widget-body-chart"
    >
      {!strategy ? <div className="mydw-state"><span>Pick a strategy in the widget settings.</span></div> : (
        data.length < 2 ? <div className="mydw-state"><span>Not enough history yet.</span></div> : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} minTickGap={28} />
              <YAxis tickFormatter={(v) => usd(v)} tick={{ fontSize: 11 }} width={72} />
              <Tooltip formatter={(v) => usd(v, { sign: true })} />
              <Legend verticalAlign="top" height={24} />
              <Line type="monotone" dataKey="mine" name="My P&L" stroke="var(--bt-667eea, #667eea)" dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="strategy" name={stratName || 'Strategy'} stroke="#22c55e" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        )
      )}
    </Shell>
  );
}
